// 우편함 창(docs/PARTY-TRADE-PRD.md 4.1, 이번엔 우편함만). 파티 보상·거래소 낙찰품이 모두 여기로 온다.
//
// ── 게임 레인이 main.ts에서 부르는 방법 ─────────────────────────────────────────
//   import { MailClient } from './market/mailClient';
//   import { mountMailUi } from './ui/mailPanel';
//   const mailUi = mountMailUi({ mail: new MailClient(), apply: applyMail /* src/game/mailApply.ts: (m) => Promise<boolean> */, inVillage: () => view.flow.scene === Scene.Village });
// ── 시그니처 ──────────────────────────────────────────────────────────────────
//   mountMailUi(host: MailUiHost): MailUi
//   MailUiHost { mail: MailClient; apply: MailApply; inVillage?: () => boolean(기본 항상 참); parent?: HTMLElement; now?: () => number; pollMs?: number(기본 60000, 0이면 열 때만 갱신) }
//   MailUi { open(); close(); toggle(); isOpen(): boolean; refresh(): Promise<void>; dispose() }
// 「우편함」 단추(menuBar)는 PARTY_MENU_EVENT('mail')로 알리고 이 창이 토글한다. 안 받은 우편이 있으면 메뉴 단추에 빨간 점(menuBadges.mail).
import { gearCard } from './gearModel';
import { assetUrl } from '../pwa/assetMap';
import type { MailApply, MailClient, MailEntry } from '../market/mailClient';
import { el } from './dom';
import { menuBadges, PARTY_MENU_EVENT } from './menuBar';
import { PARTY_WINDOW_EVENT, ensurePartyStyle } from './partyStyle';

export interface MailUiHost {
  mail: MailClient;
  apply: MailApply;
  inVillage?: () => boolean;
  parent?: HTMLElement;
  now?: () => number;
  pollMs?: number;
}
export interface MailUi { open(): void; close(): void; toggle(): void; isOpen(): boolean; refresh(): Promise<void>; dispose(): void }

const DAY = 86_400_000, HOUR = 3_600_000;
/** 남은 기간 문구 */
export function mailRemainText(m: Pick<MailEntry, 'expiresAt' | 'claimed'>, now: number): string {
  if (m.claimed) return '받기 대기 · 가방에 자리가 있으면 다시 눌러 주세요';
  const left = m.expiresAt - now;
  if (left <= 0) return '기간이 지났어요';
  if (left >= DAY) return Math.floor(left / DAY) + '일 남음';
  if (left >= HOUR) return Math.floor(left / HOUR) + '시간 남음';
  return '곧 사라져요';
}

export interface MailRowModel { id: number; name: string; sub: string; color: string; gold: boolean; icon: string; fallbacks: readonly string[]; remain: string }
/** 우편 한 통의 표시 모델: 골드는 금색 이름 + 금화 그림, 장비는 등급색 이름 + 장비 그림 */
export function mailRow(m: MailEntry, now: number): MailRowModel {
  const remain = mailRemainText(m, now);
  if (m.kind === 'item' && m.item) {
    const c = gearCard(m.item, false);
    return { id: m.id, name: c.name + (c.enh > 0 ? ' +' + c.enh : ''), sub: c.gradeName + ' ' + c.slotName + ' · ' + m.title, color: c.color, gold: false, icon: c.icon, fallbacks: c.iconFallbacks, remain };
  }
  if (m.kind === 'item') return { id: m.id, name: '알 수 없는 장비', sub: m.title, color: '#e8e8e8', gold: false, icon: 'ui/items/sword_worn.webp', fallbacks: [], remain };
  return { id: m.id, name: m.gold.toLocaleString('ko-KR') + ' 골드', sub: m.title, color: '#ffd76a', gold: true, icon: 'ui/items/cur_gold.webp', fallbacks: [], remain };
}

/** 받기 결과 한 줄 */
export function claimSummary(r: { tried: number; done: number; failed: number; stopped?: string }): { text: string; tone: '' | 'ok' | 'err' } {
  if (r.stopped === 'offline') return { text: '연결이 끊겨 멈췄어요. ' + r.done + '통을 받았어요.', tone: 'err' };
  if (r.stopped === 'rate') return { text: '너무 빨라요. 잠시 뒤에 다시 눌러 주세요.', tone: 'err' };
  if (r.stopped === 'auth') return { text: '계정 확인이 필요해요. 새로고침해 주세요.', tone: 'err' };
  if (r.tried === 0) return { text: '받을 우편이 없어요.', tone: '' };
  if (r.failed > 0) return { text: r.done + '통을 받았어요. ' + r.failed + '통은 받지 못했어요. 가방에 자리를 만든 뒤 다시 눌러 주세요.', tone: 'err' };
  return { text: r.done + '통을 모두 받았어요.', tone: 'ok' };
}

export function mountMailUi(host: MailUiHost): MailUi {
  ensurePartyStyle();
  const parent = host.parent ?? document.body;
  const now = host.now ?? Date.now;
  const village = (): boolean => { try { return host.inVillage ? host.inVillage() : true; } catch { return false; } };
  const mc = host.mail;

  const win = el('div', '', parent);
  win.className = 'dh-pw';
  win.dataset.testid = 'mail-window';
  win.setAttribute('role', 'dialog');
  win.setAttribute('aria-label', '우편함');
  const hd = el('div', '', win); hd.className = 'hd';
  el('h2', '', hd, '우편함');
  const xb = el('button', '', hd, '✕'); xb.className = 'x'; xb.type = 'button'; xb.setAttribute('aria-label', '우편함 닫기'); xb.dataset.testid = 'mail-close';
  const bd = el('div', '', win); bd.className = 'bd';
  const foot = el('div', '', win); foot.className = 'row'; foot.style.flex = '0 0 auto';

  let opened = false;
  let msg: { text: string; tone: '' | 'ok' | 'err' } = { text: '', tone: '' };
  let loadErr = '';
  let loaded = false;
  let key = '';
  let busyRow = 0;

  const unclaimed = (): number => mc.mail.length;
  const setDot = (): void => { menuBadges.mail = unclaimed() > 0; };

  function iconEl(parent2: HTMLElement, r: MailRowModel): void {
    const ic = el('div', '', parent2); ic.className = 'ic';
    const im = el('img', '', ic); im.alt = ''; im.draggable = false;
    const list = [r.icon, ...r.fallbacks];
    let i = 0;
    im.onerror = () => { i++; if (i < list.length) im.src = assetUrl(list[i]); else im.style.visibility = 'hidden'; };
    im.src = assetUrl(list[0]);
    if (!r.gold) ic.style.borderColor = r.color;
  }

  async function doClaim(id: number): Promise<void> {
    busyRow = id; key = ''; render();
    const r = await mc.claim(id, host.apply);
    busyRow = 0;
    if (r.acked) msg = { text: '받았어요.', tone: 'ok' };
    else if (r.applied) msg = { text: '받았지만 서버 확인이 늦어요. 다시 열면 정리돼요.', tone: 'err' };
    else if (r.reason) msg = { text: '받지 못했어요. 가방에 자리를 만든 뒤 다시 눌러 주세요.', tone: 'err' };
    else msg = { text: r.error === 'offline' ? '연결이 끊겨 받지 못했어요.' : r.error === 'expired' ? '기간이 지난 우편이에요.' : r.error === 'other-character' ? '다른 캐릭터가 받기 시작한 우편이에요. 그 캐릭터로 접속해 받아 주세요.' : '받지 못했어요. 잠시 뒤 다시 해 주세요.', tone: 'err' };
    await refresh(true);
  }
  async function doAll(): Promise<void> {
    msg = { text: '받는 중…', tone: '' }; key = ''; render();
    const r = await mc.claimAll(host.apply);
    msg = claimSummary(r);
    await refresh(true);
  }

  async function refresh(keepMsg = false): Promise<void> {
    const r = await mc.list();
    loaded = true;
    loadErr = r.ok ? '' : r.error === 'offline' ? '연결이 끊겨 우편을 불러오지 못했어요.' : '우편을 불러오지 못했어요. 잠시 뒤 다시 열어 주세요.';
    if (!keepMsg) msg = { text: '', tone: '' };
    setDot(); key = ''; render();
  }

  function render(): void {
    const show = opened && village();
    win.classList.toggle('on', show);
    if (!show) return;
    const k = [mc.revision, mc.busy, msg.text, loadErr, loaded, busyRow, Math.floor(now() / 60000)].join('|');
    if (k === key) return;
    key = k;
    const keep = bd.scrollTop;
    bd.textContent = ''; foot.textContent = '';
    const t = now();
    if (loadErr) { const n = el('div', '', bd, loadErr); n.className = 'note err'; }
    if (!loaded) el('div', '', bd, '불러오는 중…').className = 'note';
    else if (mc.mail.length === 0 && !loadErr) { const n = el('div', '', bd, '받을 우편이 없어요. 파티 보상은 여기로 와요.'); n.className = 'note'; n.dataset.testid = 'mail-empty'; }
    for (const m of mc.mail) {
      const r = mailRow(m, t);
      const row = el('div', '', bd); row.className = 'mail'; row.dataset.testid = 'mail-row';
      iconEl(row, r);
      const tx = el('div', '', row); tx.className = 'tx';
      const nm = el('div', '', tx, r.name); nm.className = 'nm'; nm.style.color = r.color;
      el('div', '', tx, r.sub).className = 'sub';
      el('div', '', tx, r.remain).className = 'sub';
      const b = el('button', '', row, busyRow === m.id ? '받는 중' : '받기'); b.type = 'button'; b.className = 'btn sm pri'; b.dataset.testid = 'mail-claim';
      b.disabled = mc.busy || busyRow !== 0;
      b.addEventListener('click', () => { void doClaim(m.id); });
    }
    if (msg.text) { const n = el('div', '', bd, msg.text); n.className = 'note ' + msg.tone; n.dataset.testid = 'mail-note'; }
    const all = el('button', '', foot, '모두 받기' + (mc.mail.length ? ' (' + mc.mail.length + ')' : '')); all.type = 'button'; all.className = 'btn pri grow'; all.dataset.testid = 'mail-claim-all';
    all.disabled = mc.mail.length === 0 || mc.busy || busyRow !== 0;
    all.addEventListener('click', () => { void doAll(); });
    bd.scrollTop = keep;
  }

  function setOpen(on: boolean): void {
    if (on && !village()) return;
    if (on === opened) return;
    opened = on;
    if (on) { window.dispatchEvent(new CustomEvent<string>(PARTY_WINDOW_EVENT, { detail: 'mail' })); loaded = mc.mail.length > 0; void refresh(); }
    key = ''; render();
  }

  const onMenu = (e: Event): void => { if ((e as CustomEvent<string>).detail === 'mail') setOpen(!opened); };
  const onWin = (e: Event): void => { if ((e as CustomEvent<string>).detail !== 'mail' && opened) { opened = false; key = ''; render(); } };
  window.addEventListener(PARTY_MENU_EVENT, onMenu);
  window.addEventListener(PARTY_WINDOW_EVENT, onWin);
  xb.addEventListener('click', () => setOpen(false));
  win.addEventListener('keydown', (e) => { if (e.key === 'Escape') { setOpen(false); e.stopPropagation(); } });

  // 마을에서만 가끔 목록을 갱신해 메뉴 단추의 빨간 점을 맞춘다(열려 있는 동안은 건드리지 않는다)
  const pollMs = host.pollMs ?? 60_000;
  const poll = pollMs > 0 ? setInterval(() => { if (!opened && village() && typeof document !== 'undefined' && !document.hidden) void mc.list().then(setDot); }, pollMs) : 0;
  const tick = setInterval(() => { if (opened && !village()) { opened = false; key = ''; } render(); }, 500);
  if (pollMs > 0) setTimeout(() => { if (village()) void mc.list().then(setDot); }, 1500);

  return {
    open: () => setOpen(true),
    close: () => setOpen(false),
    toggle: () => setOpen(!opened),
    isOpen: () => opened && win.classList.contains('on'),
    refresh: () => refresh(),
    dispose: () => { if (poll) clearInterval(poll); clearInterval(tick); window.removeEventListener(PARTY_MENU_EVENT, onMenu); window.removeEventListener(PARTY_WINDOW_EVENT, onWin); win.remove(); },
  };
}
